import {
  ApplicationSubmissionStatus,
  EntrataApplicationStage,
  EntrataApplicationStatus,
} from '@common/entities';

export interface ApplicationWithStatus {
  applicationStatus: EntrataApplicationStatus;
  applicationStage: EntrataApplicationStage;
  applicantCompletedOn?: string;
  submissionStatus?: string | null;
  submissionStartedAt?: string | Date | null;
  submissionCompletedAt?: string | Date | null;
}

/**
 * Determines if an application is actively being submitted to the LJ API.
 * This is ONLY used during the brief submission processing window.
 * Returns true only if explicitly marked as 'Submitting'.
 *
 * Use this to:
 * - Disable Resume button during submission
 * - Show processing spinner
 *
 * Backward compatible: NULL submission status = not currently submitting
 */
export function isApplicationSubmitting(app: ApplicationWithStatus): boolean {
  return app.submissionStatus === ApplicationSubmissionStatus.Submitting;
}

/**
 * Determines if an application is completed based on ENTRATA status.
 * Always uses the real application status from Entrata, regardless of submissionStatus.
 *
 * The submissionStatus field is ONLY for tracking the submission processing phase,
 * NOT for determining the overall application state.
 */
export function isApplicationCompleted(app: ApplicationWithStatus): boolean {
  return (
    app.applicationStatus === EntrataApplicationStatus.Completed ||
    app.applicationStage === EntrataApplicationStage.Lease ||
    (app.applicantCompletedOn !== undefined &&
      app.applicantCompletedOn !== null &&
      app.applicantCompletedOn.length > 0)
  );
}

/**
 * Determines if an application submission is complete and ready for screening checks.
 * This is used specifically for determining when to check for screening conditions.
 *
 * An application is considered submitted when:
 * 1. The submission has been successfully completed (submissionStatus = 'Completed')
 * 2. OR the application is completed per Entrata status
 *
 * This ensures screening conditions are checked immediately after submission completes,
 * not just when Entrata eventually marks the application as completed.
 */
export function isApplicationSubmitted(app: ApplicationWithStatus): boolean {
  // Check if submission was explicitly completed
  if (app.submissionStatus === ApplicationSubmissionStatus.Completed) {
    return true;
  }

  // Fall back to Entrata completion status
  return isApplicationCompleted(app);
}

/**
 * Determines if an application is in a resumable state.
 *
 * Cannot resume ONLY if:
 * - Currently submitting (processing in LJ API)
 * - Application is completed (per Entrata status)
 *
 * For everything else, use the Entrata application status to determine resumability.
 *
 * Note: submissionStatus is NOT used to determine completion - only to block
 * resume during active submission processing.
 */
export function isApplicationResumable(app: ApplicationWithStatus): boolean {
  // Cannot resume if actively submitting to LJ API
  if (isApplicationSubmitting(app)) {
    return false;
  }

  // Use Entrata status to determine if application is completed
  if (isApplicationCompleted(app)) {
    return false;
  }

  // Use Entrata status to determine if application is resumable
  // This handles ALL application states: Started, PartiallyCompleted, Failed, etc.
  return (
    app.applicationStatus === EntrataApplicationStatus.Started ||
    (app.applicationStatus === EntrataApplicationStatus.PartiallyCompleted &&
      (!app.applicantCompletedOn || app.applicantCompletedOn.length === 0))
  );
}

/**
 * Get display message for application status.
 *
 * Priority:
 * 1. If submissionStatus = 'Submitting', show processing message
 * 2. If submissionStatus = 'Failed', show submission error message
 * 3. Otherwise, use Entrata status for all other states
 *
 * This ensures submissionStatus is only used during the brief submission window.
 */
export function getApplicationStatusMessage(app: ApplicationWithStatus): {
  message: string;
  variant: 'info' | 'success' | 'warning' | 'error';
  showSpinner: boolean;
} {
  // ONLY check submissionStatus for the submission processing phase
  if (isApplicationSubmitting(app)) {
    return {
      message: 'Processing your application submission. This may take a few moments.',
      variant: 'info',
      showSpinner: true,
    };
  }

  // Show submission failure message if just failed
  if (app.submissionStatus === ApplicationSubmissionStatus.Failed) {
    return {
      message:
        'There was an issue submitting your application. You can try again or contact support.',
      variant: 'error',
      showSpinner: false,
    };
  }

  // For all other cases, use ENTRATA status
  // (This includes Completed, Started, PartiallyCompleted, Approved, Denied, etc.)
  if (isApplicationCompleted(app)) {
    return {
      message: 'Application submitted successfully.',
      variant: 'success',
      showSpinner: false,
    };
  }

  if (app.applicationStatus === EntrataApplicationStatus.Started) {
    return {
      message: 'Application in progress.',
      variant: 'info',
      showSpinner: false,
    };
  }

  // Default: Show whatever Entrata reports
  return {
    message: `Application status: ${app.applicationStatus}`,
    variant: 'info',
    showSpinner: false,
  };
}

/**
 * Determines if we should poll for submission status updates.
 * Only poll if explicitly marked as 'Submitting'.
 *
 * This prevents unnecessary API calls. Polling is ONLY needed during the brief
 * window when the application is being submitted to the LJ API.
 *
 * Once submissionStatus becomes 'Completed' or 'Failed' (or is NULL), stop polling
 * and rely on the regular applicant/applications endpoint for status updates.
 */
export function shouldPollSubmissionStatus(app: ApplicationWithStatus): boolean {
  return app.submissionStatus === ApplicationSubmissionStatus.Submitting;
}
